import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONDITIONS = ['New', 'Excellent', 'Very Good', 'Good'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF'];

export default function SellerDashboard() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    brand: '',
    model: '',
    ref_number: '',
    year: new Date().getFullYear(),
    condition: 'Excellent',
    price: '',
    currency: 'USD',
    location: '',
    case_size: '',
    material: '',
    movement: '',
    dial_color: '',
    strap: '',
    box_papers: '',
    description: '',
    images: [],
    status: 'draft'
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/seller/inventory`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price),
        year: parseInt(formData.year),
        images: formData.images.filter(img => img.trim() !== '')
      };

      if (editingListing) {
        await axios.patch(`${API_URL}/api/listings/${editingListing.id}`, dataToSend);
        toast.success('Listing updated successfully');
      } else {
        await axios.post(`${API_URL}/api/listings`, dataToSend);
        toast.success('Listing created successfully');
      }

      setDialogOpen(false);
      setEditingListing(null);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error('Error saving listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      title: listing.title,
      brand: listing.brand,
      model: listing.model,
      ref_number: listing.ref_number,
      year: listing.year,
      condition: listing.condition,
      price: listing.price.toString(),
      currency: listing.currency,
      location: listing.location,
      case_size: listing.case_size,
      material: listing.material,
      movement: listing.movement,
      dial_color: listing.dial_color,
      strap: listing.strap,
      box_papers: listing.box_papers,
      description: listing.description,
      images: listing.images || [],
      status: listing.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/listings/${listingId}`);
      toast.success('Listing deleted successfully');
      fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      brand: '',
      model: '',
      ref_number: '',
      year: new Date().getFullYear(),
      condition: 'Excellent',
      price: '',
      currency: 'USD',
      location: '',
      case_size: '',
      material: '',
      movement: '',
      dial_color: '',
      strap: '',
      box_papers: '',
      description: '',
      images: [],
      status: 'draft'
    });
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ''] });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'bg-muted text-foreground',
      pending: 'bg-yellow-500/20 text-yellow-500',
      active: 'bg-green-500/20 text-green-500',
      rejected: 'bg-red-500/20 text-red-500',
      sold: 'bg-blue-500/20 text-blue-500'
    };

    return (
      <Badge className={variants[status]} data-testid={`listing-status-${status}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="seller-dashboard-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2">
              Seller Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your watch listings
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setEditingListing(null); }} data-testid="create-listing-button">
                <Plus className="mr-2 h-4 w-4" />
                Add Listing
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="listing-dialog">
              <DialogHeader>
                <DialogTitle>{editingListing ? 'Edit Listing' : 'Create New Listing'}</DialogTitle>
                <DialogDescription>
                  {editingListing ? 'Update your watch listing details' : 'Fill in the details to create a new watch listing'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        data-testid="listing-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="brand">Brand *</Label>
                      <Input
                        id="brand"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        required
                        data-testid="listing-brand-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="model">Model *</Label>
                      <Input
                        id="model"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        required
                        data-testid="listing-model-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ref_number">Reference Number *</Label>
                      <Input
                        id="ref_number"
                        value={formData.ref_number}
                        onChange={(e) => setFormData({ ...formData, ref_number: e.target.value })}
                        required
                        data-testid="listing-ref-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="year">Year *</Label>
                      <Input
                        id="year"
                        type="number"
                        min="1900"
                        max={new Date().getFullYear()}
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        required
                        data-testid="listing-year-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="condition">Condition *</Label>
                      <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                        <SelectTrigger data-testid="listing-condition-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDITIONS.map(cond => (
                            <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">Price *</Label>
                      <Input
                        id="price"
                        type="number"
                        min="0"
                        step="100"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        data-testid="listing-price-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="location">Location *</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        required
                        data-testid="listing-location-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case_size">Case Size *</Label>
                      <Input
                        id="case_size"
                        placeholder="e.g., 40mm"
                        value={formData.case_size}
                        onChange={(e) => setFormData({ ...formData, case_size: e.target.value })}
                        required
                        data-testid="listing-case-size-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="material">Material *</Label>
                      <Input
                        id="material"
                        value={formData.material}
                        onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                        required
                        data-testid="listing-material-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="movement">Movement *</Label>
                      <Input
                        id="movement"
                        value={formData.movement}
                        onChange={(e) => setFormData({ ...formData, movement: e.target.value })}
                        required
                        data-testid="listing-movement-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dial_color">Dial Color *</Label>
                      <Input
                        id="dial_color"
                        value={formData.dial_color}
                        onChange={(e) => setFormData({ ...formData, dial_color: e.target.value })}
                        required
                        data-testid="listing-dial-color-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="strap">Strap/Bracelet *</Label>
                      <Input
                        id="strap"
                        value={formData.strap}
                        onChange={(e) => setFormData({ ...formData, strap: e.target.value })}
                        required
                        data-testid="listing-strap-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="box_papers">Box & Papers *</Label>
                    <Input
                      id="box_papers"
                      placeholder="e.g., Box & Papers, Box Only, Watch Only"
                      value={formData.box_papers}
                      onChange={(e) => setFormData({ ...formData, box_papers: e.target.value })}
                      required
                      data-testid="listing-box-papers-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      data-testid="listing-description-textarea"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Image URLs</Label>
                    {formData.images.map((img, index) => (
                      <Input
                        key={index}
                        value={img}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        data-testid={`listing-image-input-${index}`}
                      />
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addImageField} data-testid="add-image-button">
                      Add Image URL
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="listing-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending">Submit for Approval</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="listing-submit-button">
                    {editingListing ? 'Update' : 'Create'} Listing
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Listings Table */}
        <Card className="border-border bg-card" data-testid="listings-table-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Watch</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No listings yet. Create your first listing to get started.
                  </TableCell>
                </TableRow>
              ) : (
                listings.map((listing) => (
                  <TableRow key={listing.id} data-testid="listing-row">
                    <TableCell>
                      <div>
                        <p className="font-medium text-foreground">{listing.brand} {listing.model}</p>
                        <p className="text-sm text-muted-foreground">{listing.title}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{listing.ref_number}</TableCell>
                    <TableCell className="font-medium">{formatPrice(listing.price, listing.currency)}</TableCell>
                    <TableCell>{getStatusBadge(listing.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(listing)} data-testid="edit-listing-button">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(listing.id)} data-testid="delete-listing-button">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
